<template>
  <div class="location-search">
    <div class="input-wrapper">
      <input
        type="text"
        v-model="query"
        placeholder="Search location..."
        @input="debouncedSearch"
        @keydown.enter="handleEnter"
        @blur="handleBlur"
      />
      <button
        v-if="query.length > 0"
        type="button"
        class="clear-button"
        aria-label="Clear search"
        @mousedown.prevent="clearQuery"
      >
        &times;
      </button>
    </div>
    <ul v-if="results.length > 0" class="results">
      <li v-for="(result, index) in results" :key="index" @mousedown="select(result)">
        {{ result.label }}
      </li>
    </ul>
  </div>
</template>

<script>
import { ref } from 'vue';
import { OpenStreetMapProvider } from 'leaflet-geosearch';
import debounce from 'lodash/debounce';

export default {
  name: 'LocationSearchBox',
  emits: ['select'],
  setup(props, { emit }) {
    const provider = new OpenStreetMapProvider();
    const query = ref('');
    const results = ref([]);

    const search = async () => {
      if (!query.value.trim()) {
        results.value = [];
        return;
      }
      try {
        results.value = await provider.search({ query: query.value });
      } catch (error) {
        console.error('Error searching for location:', error);
        results.value = [];
      }
    };

    const debouncedSearch = debounce(search, 300);

    const select = (result) => {
      emit('select', { lat: result.y, lng: result.x, bounds: result.bounds, label: result.label });
      results.value = [];
      query.value = result.label;
    };

    // Enter previously just (re-)populated the suggestions dropdown without navigating
    // anywhere — pressing it looked like nothing happened. Now it jumps to the top result,
    // the same as clicking it would.
    const handleEnter = async () => {
      if (results.value.length === 0) {
        await search();
      }
      if (results.value.length > 0) {
        select(results.value[0]);
      }
    };

    const handleBlur = () => {
      // Delay so a click on a suggestion registers before the list is cleared.
      setTimeout(() => {
        results.value = [];
      }, 200);
    };

    const clearQuery = () => {
      query.value = '';
      results.value = [];
    };

    return { query, results, search, debouncedSearch, select, handleEnter, handleBlur, clearQuery };
  },
};
</script>

<style scoped>
.location-search {
  position: relative;
  width: 100%;
}

.input-wrapper {
  position: relative;
  width: 100%;
}

input {
  width: 100%;
  box-sizing: border-box;
  padding: 8px 28px 8px 10px;
  border: 1px solid #ccc;
  border-radius: 6px;
  font-size: 14px;
}

.clear-button {
  position: absolute;
  top: 0;
  right: 0;
  bottom: 0;
  width: 28px;
  border: none;
  background: none;
  color: #888;
  font-size: 18px;
  line-height: 1;
  cursor: pointer;
}

.clear-button:hover {
  color: #333;
}

.results {
  list-style: none;
  margin: 4px 0 0;
  padding: 0;
  max-height: 200px;
  overflow-y: auto;
  background: white;
  border: 1px solid #ddd;
  border-radius: 6px;
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.15);
}

.results li {
  padding: 8px 10px;
  cursor: pointer;
  border-bottom: 1px solid #eee;
  font-size: 13px;
}

.results li:last-child {
  border-bottom: none;
}

.results li:hover {
  background-color: #f0f0f0;
}
</style>
